import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminDashboard,
  adminMcpConnectionsOutput,
  adminMcpRevokeOutput,
  oauthDecisionOutput,
  oauthRequestView,
  adminEventOutput,
  adminInsuranceStatements,
  adminFormat,
  adminFormatsOutput,
  adminRegistrationDetail,
  adminRegistrationsPage,
  adminNote,
  adminTeamDetail,
  adminTeamsOutput,
  adminVoucher,
  adminVouchersOutput,
  availableFormats,
  createCheckoutOutput,
  orderSummary,
  profileOrders,
  publicEvent,
  referralSummary,
  routes,
  sendInvitesOutput,
  sessionUser,
  voucherPreview,
  type AdminRegistrationFilters,
  type CreateFormatInput,
  type ReplaceTierInput,
  type TierInput,
  type UpdateFormatInput,
  type CreateVoucherInput,
  type UpdateVoucherInput,
  type SetTeamCaptainInput,
  type AdminTeamFilters,
  type RenameTeamInput,
  type ReplaceTeamSeatInput,
  type MoveTeamMemberInput,
  type UpdateAdminEventInput,
  type CreateAdminNoteInput,
  type CreateCheckoutInput,
  type RequestMagicLinkInput,
  type SendInvitesInput,
  addTeamMembersOutput,
  joinTeamView,
  myTeams,
  teamSummary,
  type AddTeamMembersInput,
  profileResponse,
  adminReviewQueue,
  type ReviewDocumentInput,
  type ReviewLicenceInput,
  type UpdateProfileInput,
} from '@gg/shared';
import { api } from './api';
import { uploadCertificate } from './documentUpload';
import { config } from './config';
import { orderOutcome } from './orderOutcome';

export const useEvent = () =>
  useQuery({
    queryKey: ['event', config.eventSlug],
    queryFn: () => api(routes.event(config.eventSlug), { schema: publicEvent }),
    staleTime: 30_000,
  });

/**
 * `pollMs`: refetch at this interval while the success page is waiting for the webhook.
 * `taken`: Stripe said the payment went through, so a failed or expired order still waits
 * (FE-01, `orderOutcome`).
 */
export const useOrder = (orderId: string | undefined, pollMs?: number, taken = false) =>
  useQuery({
    queryKey: ['order', orderId],
    queryFn: () => api(routes.order(orderId!), { schema: orderSummary }),
    enabled: Boolean(orderId),
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return pollMs && status && orderOutcome(status, taken) === 'waiting' ? pollMs : false;
    },
  });

export const useMe = () =>
  useQuery({
    queryKey: ['me'],
    queryFn: () => api(routes.me, { schema: sessionUser }),
    retry: false,
  });

/** Profile: paid and refunded orders with their races, receipt and insurance (P-10). */
export const useMyOrders = (enabled = true) =>
  useQuery({
    queryKey: ['me', 'orders'],
    queryFn: () => api(routes.meOrders, { schema: profileOrders }),
    enabled,
  });

/** Upsell: formats not bought yet (P-13). Only for a logged-in participant. */
export const useAvailableFormats = (enabled: boolean) =>
  useQuery({
    queryKey: ['me', 'available-formats'],
    queryFn: () => api(routes.meAvailableFormats, { schema: availableFormats }),
    enabled,
    retry: false,
  });

export const useReferral = (enabled = true) =>
  useQuery({
    queryKey: ['referral'],
    queryFn: () => api(routes.meReferral, { schema: referralSummary }),
    enabled,
  });

/** Confirmation screen: exchange the checkout claim token for a session once the order is paid. */
export const useClaimOrderSession = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, token }: { orderId: string; token: string }) =>
      api(routes.orderSession(orderId), { method: 'POST', body: { token }, schema: sessionUser }),
    onSuccess: (user) => qc.setQueryData(['me'], user),
  });
};

/** "Resend the email" on the confirmation screen (record 0026, D5): always a neutral 202. */
export const useResendConfirmation = () =>
  useMutation({
    mutationFn: ({ orderId, token }: { orderId: string; token: string }) =>
      api<void>(routes.orderResendConfirmation(orderId), { method: 'POST', body: { token } }),
  });

/** Discount preview for the applied code on screen 02 (P-03). Display only. */
export const useVoucherPreview = (
  eventId: string | undefined,
  code: string | undefined,
  formatIds: string[],
  /** Teammate's checkout (P-21): the place is priced at the team's locked tier. */
  joinCode?: string,
) =>
  useQuery({
    queryKey: ['voucher', eventId, code, [...formatIds].sort(), joinCode ?? null],
    queryFn: () =>
      api(routes.vouchersValidate, {
        method: 'POST',
        body: { eventId, code, formatIds, joinCode },
        schema: voucherPreview,
      }),
    enabled: Boolean(eventId && code && formatIds.length > 0),
    retry: false,
    staleTime: 60_000,
  });

export const useCreateCheckout = () =>
  useMutation({
    mutationFn: (body: CreateCheckoutInput) =>
      api(routes.checkout, { method: 'POST', body, schema: createCheckoutOutput }),
  });

export const useRequestMagicLink = () =>
  useMutation({
    mutationFn: (body: RequestMagicLinkInput) =>
      api<void>(routes.authRequest, { method: 'POST', body }),
  });

export const useVerifyMagicLink = () =>
  useMutation({
    mutationFn: (token: string) =>
      api<{ redirectTo: string }>(routes.authVerify, { method: 'POST', body: { token } }),
  });

export const useSendInvites = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SendInvitesInput) =>
      api(routes.meInvites, { method: 'POST', body, schema: sendInvitesOutput }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['referral'] }),
  });
};

/** Teams the participant captains or joined, for the profile (P-20, P-21). */
export const useMyTeams = (enabled = true) =>
  useQuery({
    queryKey: ['me', 'teams'],
    queryFn: () => api(routes.meTeams, { schema: myTeams }),
    enabled,
  });

export const useTeam = (teamId: string | undefined) =>
  useQuery({
    queryKey: ['me', 'teams', teamId],
    queryFn: () => api(routes.meTeam(teamId!), { schema: teamSummary }),
    enabled: Boolean(teamId),
  });

export const useResendTeamMember = (teamId: string) =>
  useMutation({
    mutationFn: (memberId: string) =>
      api<void>(routes.meTeamMemberResend(teamId, memberId), { method: 'POST' }),
  });

export const useReplaceTeamMember = (teamId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      api<void>(routes.meTeamMember(teamId, memberId), { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'teams'] }),
  });
};

/** Join screen (07): the seat behind a join code. 410 = dead link. */
export const useJoinTeamView = (code: string | undefined) =>
  useQuery({
    queryKey: ['teams', 'join', code],
    queryFn: () => api(routes.teamsJoin(code!), { schema: joinTeamView }),
    enabled: Boolean(code),
    retry: false,
  });

/** Captain adds teammate emails to their team (P-20), after payment or from the profile. */
export const useAddTeamMembers = (teamId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddTeamMembersInput) =>
      api(routes.meTeamMembers(teamId), { method: 'POST', body, schema: addTeamMembersOutput }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'teams'] }),
  });
};

export const useLogout = () =>
  useMutation({ mutationFn: () => api<void>(routes.authLogout, { method: 'POST' }) });

// Back office (A-03, A-04, A-06). The page guard reads `useAdminMe`: 401 = sign in, 403 = not allow-listed.
export const useAdminMe = () =>
  useQuery({
    queryKey: ['admin', 'me'],
    queryFn: () => api<{ email: string }>(routes.adminMe),
    retry: false,
  });

export const useAdminRegistrations = (
  filters: AdminRegistrationFilters & { page: number; pageSize: number },
) =>
  useQuery({
    queryKey: ['admin', 'registrations', filters],
    queryFn: () =>
      api(`${routes.adminRegistrations}?${adminQueryString(filters)}`, {
        schema: adminRegistrationsPage,
      }),
    placeholderData: keepPreviousData,
  });

export const useAdminRegistration = (id: string | undefined) =>
  useQuery({
    queryKey: ['admin', 'registration', id],
    queryFn: () => api(routes.adminRegistration(id!), { schema: adminRegistrationDetail }),
    enabled: Boolean(id),
  });

export const useAddAdminNote = (registrationId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAdminNoteInput) =>
      api(routes.adminRegistrationNotes(registrationId), {
        method: 'POST',
        body,
        schema: adminNote,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'registration', registrationId] }),
  });
};

// Event settings (A-13).
export const useAdminEvent = () =>
  useQuery({
    queryKey: ['admin', 'event'],
    queryFn: () => api(routes.adminEvent, { schema: adminEventOutput }),
  });

/** The insurer's unpaid premium statements (decision 0025); only asked when it is configured. */
export const useAdminInsuranceStatements = (enabled: boolean) =>
  useQuery({
    queryKey: ['admin', 'insurance', 'statements'],
    queryFn: () => api(routes.adminInsuranceStatements, { schema: adminInsuranceStatements }),
    enabled,
    retry: false,
  });

export const useUpdateAdminEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateAdminEventInput) =>
      api(routes.adminEvent, { method: 'PATCH', body, schema: adminEventOutput }),
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'event'], data);
      // The checkout reads the same columns: refresh this tab's public event too.
      return qc.invalidateQueries({ queryKey: ['event'] });
    },
  });
};

// Formats and pricing tiers (A-01). Every write refreshes the list and the public event.
export const useAdminFormats = () =>
  useQuery({
    queryKey: ['admin', 'formats'],
    queryFn: () => api(routes.adminFormats, { schema: adminFormatsOutput }),
  });

function useFormatWrite<V, R>(request: (vars: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['admin', 'formats'] }),
        qc.invalidateQueries({ queryKey: ['admin', 'registrations'] }),
        qc.invalidateQueries({ queryKey: ['event'] }),
      ]),
  });
}

export const useCreateFormat = () =>
  useFormatWrite((body: CreateFormatInput) =>
    api(routes.adminFormats, { method: 'POST', body, schema: adminFormat }),
  );

export const useUpdateFormat = (id: string) =>
  useFormatWrite((body: UpdateFormatInput) =>
    api(routes.adminFormat(id), { method: 'PATCH', body, schema: adminFormat }),
  );

export const useDeleteFormat = (id: string) =>
  useFormatWrite(() => api<void>(routes.adminFormat(id), { method: 'DELETE' }));

export const useCreateTier = (formatId: string) =>
  useFormatWrite((body: TierInput) =>
    api(routes.adminFormatTiers(formatId), { method: 'POST', body, schema: adminFormat }),
  );

export const useUpdateTier = (formatId: string) =>
  useFormatWrite(({ tierId, body }: { tierId: string; body: TierInput }) =>
    api(routes.adminFormatTier(formatId, tierId), { method: 'PATCH', body, schema: adminFormat }),
  );

export const useDeleteTier = (formatId: string) =>
  useFormatWrite((tierId: string) =>
    api(routes.adminFormatTier(formatId, tierId), { method: 'DELETE', schema: adminFormat }),
  );

export const useReplaceTier = (formatId: string) =>
  useFormatWrite(({ tierId, body }: { tierId: string; body: ReplaceTierInput }) =>
    api(routes.adminFormatTierReplace(formatId, tierId), {
      method: 'POST',
      body,
      schema: adminFormat,
    }),
  );

// Dashboard (A-09): read-only, refreshed every minute while the page is open.
export const useAdminDashboard = () =>
  useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => api(routes.adminDashboard, { schema: adminDashboard }),
    refetchInterval: 60_000,
  });

// Voucher codes (A-02). Checkout reads them live: nothing public to refresh.
export const useAdminVouchers = () =>
  useQuery({
    queryKey: ['admin', 'vouchers'],
    queryFn: () => api(routes.adminVouchers, { schema: adminVouchersOutput }),
  });

function useVoucherWrite<V, R>(request: (vars: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'vouchers'] }),
  });
}

export const useCreateVoucher = () =>
  useVoucherWrite((body: CreateVoucherInput) =>
    api(routes.adminVouchers, { method: 'POST', body, schema: adminVoucher }),
  );

export const useUpdateVoucher = () =>
  useVoucherWrite(({ id, body }: { id: string; body: UpdateVoucherInput }) =>
    api(routes.adminVoucher(id), { method: 'PATCH', body, schema: adminVoucher }),
  );

export const useDeleteVoucher = () =>
  useVoucherWrite((id: string) => api<void>(routes.adminVoucher(id), { method: 'DELETE' }));

// Teams (A-12, first slice): list, detail, captain appointment.
export const useAdminTeams = (filters: AdminTeamFilters) =>
  useQuery({
    queryKey: ['admin', 'teams', filters],
    queryFn: () =>
      api(`${routes.adminTeams}?${adminQueryString(filters)}`, { schema: adminTeamsOutput }),
    placeholderData: keepPreviousData,
  });

export const useAdminTeam = (id: string | undefined) =>
  useQuery({
    queryKey: ['admin', 'team', id],
    queryFn: () => api(routes.adminTeam(id!), { schema: adminTeamDetail }),
    enabled: !!id,
  });

export const useSetTeamCaptain = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetTeamCaptainInput) =>
      api(routes.adminTeamCaptain(id), { method: 'POST', body, schema: adminTeamDetail }),
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'team', id], data);
      return qc.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
  });
};

export const useRenameTeam = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RenameTeamInput) =>
      api(routes.adminTeam(id), { method: 'PATCH', body, schema: adminTeamDetail }),
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'team', id], data);
      return qc.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
  });
};

export const useReplaceTeamSeat = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, body }: { memberId: string; body: ReplaceTeamSeatInput }) =>
      api(routes.adminTeamMemberReplace(id, memberId), {
        method: 'POST',
        body,
        schema: adminTeamDetail,
      }),
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'team', id], data);
      return qc.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
  });
};

export const useMoveTeamMember = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, body }: { memberId: string; body: MoveTeamMemberInput }) =>
      api(routes.adminTeamMemberMove(id, memberId), {
        method: 'POST',
        body,
        schema: adminTeamDetail,
      }),
    onSuccess: async (data, { body }) => {
      qc.setQueryData(['admin', 'team', id], data);
      await qc.invalidateQueries({ queryKey: ['admin', 'team', body.teamId] });
      return qc.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
  });
};

/** Cancel or reopen a team (record 0024 Q4). */
export const useSetTeamCancelled = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cancel: boolean) =>
      api(cancel ? routes.adminTeamCancel(id) : routes.adminTeamReopen(id), {
        method: 'POST',
        schema: adminTeamDetail,
      }),
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'team', id], data);
      return qc.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
  });
};

export const useResendTeamSeat = (id: string) =>
  useMutation({
    mutationFn: (memberId: string) =>
      api<void>(routes.adminTeamMemberResend(id, memberId), { method: 'POST' }),
  });

/** Query string for the list and the CSV export: set filters only, in a stable order. */
export function adminQueryString(f: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== '') params.set(k, String(v));
  return params.toString();
}

// Back-office MCP (issue #47): the consent page of a pending authorization, and MCP access in
// the settings. Approve / deny return where the browser goes next (the client's redirect URI).
export const useOAuthRequest = (id: string | null) =>
  useQuery({
    queryKey: ['oauth', 'request', id],
    queryFn: () => api(routes.oauthRequest(id!), { schema: oauthRequestView }),
    enabled: !!id,
    retry: false,
  });

export const useOAuthDecision = () =>
  useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      api(approve ? routes.oauthRequestApprove(id) : routes.oauthRequestDeny(id), {
        method: 'POST',
        schema: oauthDecisionOutput,
      }),
  });

export const useAdminMcpConnections = () =>
  useQuery({
    queryKey: ['admin', 'mcp'],
    queryFn: () => api(routes.adminMcpConnections, { schema: adminMcpConnectionsOutput }),
  });

/** Revoke one connection by id, or every one of the event when `id` is null. */
export const useRevokeMcp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | null) =>
      api(id ? routes.adminMcpConnection(id) : routes.adminMcpConnections, {
        method: 'DELETE',
        schema: adminMcpRevokeOutput,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'mcp'] }),
  });
};

// Pre-race profile (P-11) and medical certificate (P-12).
const profileKey = ['me', 'profile'] as const;

export const useProfile = (enabled = true) =>
  useQuery({
    queryKey: profileKey,
    queryFn: () => api(routes.meProfile, { schema: profileResponse }),
    enabled,
  });

/** Autosave of one field: the answer is the whole profile with its new completion. */
export const useSaveProfileField = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProfileInput) =>
      api(routes.meProfile, { method: 'PATCH', body, schema: profileResponse }),
    onSuccess: (profile) => qc.setQueryData(profileKey, profile),
  });
};

export const useUploadCertificate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: uploadCertificate,
    onSettled: () => qc.invalidateQueries({ queryKey: profileKey }),
  });
};

// Document review queue (A-05): certificates and licences waiting for the organiser.
export const useAdminReviewQueue = () =>
  useQuery({
    queryKey: ['admin', 'documents'],
    queryFn: () => api(routes.adminDocuments, { schema: adminReviewQueue }),
  });

/** A review changes the queue, the dashboard count and the registrations' document status. */
function useReviewWrite<V>(request: (vars: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['admin', 'documents'] }),
        qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] }),
        qc.invalidateQueries({ queryKey: ['admin', 'registrations'] }),
        qc.invalidateQueries({ queryKey: ['admin', 'registration'] }),
      ]),
  });
}

export const useReviewDocument = () =>
  useReviewWrite(({ id, body }: { id: string; body: ReviewDocumentInput }) =>
    api(routes.adminDocumentReview(id), { method: 'POST', body }),
  );

export const useReviewLicence = () =>
  useReviewWrite(({ userId, body }: { userId: string; body: ReviewLicenceInput }) =>
    api(routes.adminLicenceReview(userId), { method: 'POST', body }),
  );
